/**
 * Inline product search for mail order line items (uses catalog already loaded in modal).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  queryMatchesSearchText,
  productRowSearchHaystack,
  scoreProductSearchRelevance,
} from '../utils/siteSearch';
import { getRetailPriceCents } from '../utils/wholesalePricing';

const formatRetail = (product) => `$${(getRetailPriceCents(product) / 100).toFixed(2)}`;

const MailOrderLineProductPicker = ({
  products,
  value,
  onSelect,
  placeholder = 'Search by name, SKU, ID, author…',
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(value)),
    [products, value]
  );

  const results = useMemo(() => {
    const trimmed = query.trim();
    let list = products;

    if (trimmed) {
      const onlyDigits = /^\d+$/.test(trimmed);
      if (onlyDigits) {
        list = products.filter((p) => {
          const idStr = String(p.id ?? '');
          return idStr === trimmed || idStr.includes(trimmed) || Number(p.id) === Number(trimmed);
        });
        list.sort((a, b) => {
          const rank = (p) => {
            const idStr = String(p.id ?? '');
            if (idStr === trimmed || Number(p.id) === Number(trimmed)) return 0;
            if (idStr.startsWith(trimmed)) return 1;
            return 2;
          };
          return rank(a) - rank(b);
        });
      } else {
        list = products.filter((p) =>
          queryMatchesSearchText(productRowSearchHaystack(p), trimmed, { title: p.name })
        );
        list = [...list].sort(
          (a, b) => scoreProductSearchRelevance(b, trimmed) - scoreProductSearchRelevance(a, trimmed)
        );
      }
    }

    return list.slice(0, 15);
  }, [products, query]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const pickProduct = (product) => {
    onSelect(String(product.id));
    setQuery('');
    setOpen(false);
  };

  const clearSelection = () => {
    onSelect('');
    setQuery('');
    setOpen(true);
  };

  const openSearch = () => {
    setQuery(selectedProduct?.name || '');
    setOpen(true);
  };

  if (selectedProduct && !open) {
    return (
      <div className="mail-order-product-pick is-selected" ref={rootRef}>
        <button
          type="button"
          className="mail-order-product-pick__selected"
          onClick={openSearch}
          title="Click to change product"
        >
          <span className="mail-order-product-pick__name">{selectedProduct.name}</span>
          <span className="mail-order-product-pick__meta">
            #{selectedProduct.id}
            {selectedProduct.sku ? ` · ${selectedProduct.sku}` : ''}
            {' · '}
            {formatRetail(selectedProduct)}
            {(selectedProduct.hide_from_shop === 1 || selectedProduct.hide_from_shop === true) && (
              <span className="mail-order-product-pick__hidden"> · shop hidden</span>
            )}
          </span>
        </button>
        <button
          type="button"
          className="mail-order-product-pick__clear"
          onClick={clearSelection}
          aria-label="Clear product"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className={`mail-order-product-pick${open ? ' is-open' : ''}`} ref={rootRef}>
      <input
        type="search"
        className="mail-order-product-pick__input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        aria-expanded={open}
        aria-haspopup="listbox"
      />
      {open && (
        <ul className="mail-order-product-pick__dropdown" role="listbox">
          {results.length === 0 ? (
            <li className="mail-order-product-pick__empty" role="option">
              {products.length ? 'No matches — try name, SKU, or product ID' : 'Loading catalog…'}
            </li>
          ) : (
            results.map((product) => (
              <li key={product.id} role="option">
                <button type="button" onClick={() => pickProduct(product)}>
                  <span className="mail-order-product-pick__result-name">{product.name}</span>
                  <span className="mail-order-product-pick__result-meta">
                    #{product.id}
                    {product.sku ? ` · SKU ${product.sku}` : ''}
                    {product.author || product.artist ? ` · ${product.author || product.artist}` : ''}
                    {' · '}
                    {formatRetail(product)}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default MailOrderLineProductPicker;
